import { useState, useRef, useEffect } from "react";
import { ResultOf } from "gql.tada";
import { useAnimate, useMotionValue } from "motion/react";
import ArrowIcon from "@hawkins/assets/lib/icons/iconArrowUpRight";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { animationCurves } from "../../../utils/animationCurves";
import { PartnershipListItemFragment } from "../../../queries/fragments/PartnershipListItemFragment";
import { Button } from "../../shared/core/Button";
import { Link } from "../../shared/core/Link";
import { LinkFragment } from "../../../queries/fragments/LinkFragment";
import { RevealAnimationVariants } from "../../../utils/RevealAnimationVariants";
import { MarkdownLite } from "../../shared/MarkdownLite";

type TPartnershipListItem = ResultOf<typeof PartnershipListItemFragment>;

type PartnershipListItemProps = {
  item: TPartnershipListItem;
  index: number;
  height: number;
  centeredItem: HTMLDivElement | null;
  mobileVpObserver?: IntersectionObserver;
  onMouseOver: (idx: number) => void;
};

const PartnershipListItem = ({
  item,
  index,
  height,
  centeredItem,
  mobileVpObserver,
  onMouseOver,
}: PartnershipListItemProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDesktop = useIsDesktop();
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Observe intersection between this section and the
    // viewport, in mobile, so we play the video when the
    // section is completely visible
    if (!isDesktop) {
      const element = elementRef.current;
      if (element && mobileVpObserver) {
        mobileVpObserver.observe(element);
      }

      return () => {
        if (element && mobileVpObserver) {
          mobileVpObserver?.unobserve(element);
        }
      };
    }
  }, [mobileVpObserver, isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      // For mobile, reproduce the video if the section is
      // completely inside the viewport
      if (elementRef.current?.isEqualNode(centeredItem)) {
        setIsPlaying(true);
        videoRef?.current?.play();
      } else {
        setIsPlaying(false);
        if (videoRef?.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }
    }
  }, [centeredItem, isDesktop]);

  const handleMouseOver = () => {
    // Handle video play for desktop
    if (!isDesktop) return;
    setIsPlaying(true);
    videoRef?.current?.play();
    onMouseOver(index);
  };

  const handleMouseOut = () => {
    if (!isDesktop) return;
    if (videoRef?.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <Link link={item.link as unknown as ResultOf<typeof LinkFragment>}>
      <div
        ref={elementRef}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
        className="partnership-item w-full flex flex-col dsk:flex-row dsk:justify-between py-8 px-4 dsk:px-36 z-10"
        style={{
          height: isDesktop ? height : "inherit",
        }}
      >
        {/* Content */}
        {isDesktop ? (
          <DesktopContent item={item} isPlaying={isPlaying} />
        ) : (
          <MobileContent item={item} />
        )}
        {/* Media */}
        <div className="w-full dsk:w-[300px] flex flex-col justify-center">
          {!!item?.videoPosterImage?.url && !!item?.video?.url && (
            <div className="relative rounded-lg aspect-[4/3] h-[206px]">
              {/* Poster */}
              <PosterWithLogo item={item} isPlaying={isPlaying} />
              {/* Video */}
              <video
                ref={videoRef}
                className="absolute top-0 left-0 h-full object-cover rounded-lg transition duration-long"
                style={{
                  opacity: isPlaying ? 1 : 0,
                  transform: `scale(${isPlaying && isDesktop ? 1.1 : 1})`,
                }}
                src={item?.video?.url}
                playsInline
                loop
                muted
              />
            </div>
          )}
        </div>
        {/* Mobile CTA */}
        {!isDesktop && (
          <Button className="mt-5" asChild>
            <div className="flex items-center gap-2">
              <span>{item.exploreCta}</span>
              <ArrowIcon size="small" />
            </div>
          </Button>
        )}
      </div>
    </Link>
  );
};

const DesktopContent = ({
  item,
  isPlaying,
}: {
  item: TPartnershipListItem;
  isPlaying: boolean;
}) => {
  const [scope, animate] = useAnimate();
  const titleY = useMotionValue(0);

  useEffect(() => {
    if (isPlaying) {
      animate(titleY, -74, animationCurves.macro);
    } else {
      animate(titleY, 0, animationCurves.macro);
    }
  }, [isPlaying, animate, titleY]);

  return (
    <div className="flex flex-col gap-10 justify-center z-10">
      <p className="text-brand-cream">
        <MarkdownLite>{item?.eyebrowText}</MarkdownLite>
      </p>
      <div ref={scope} className="flex flex-col gap-2 relative transition">
        <h3 className="text-heading-3 text-brand-cream">{item.title}</h3>
        <h3
          className="text-heading-3 absolute inset-0 select-none"
          aria-hidden
          style={{
            transitionTimingFunction: `cubic-bezier(${animationCurves.macro.ease.join(
              ", "
            )})`,
            transitionProperty: RevealAnimationVariants.clipPath.cssProperty,
            transitionDuration: `${animationCurves.macro.duration}s`,
            [RevealAnimationVariants.clipPath.property]:
              RevealAnimationVariants.clipPath.variant[
                isPlaying ? "hidden" : "visible"
              ],
          }}
        >
          {item.title}
        </h3>
      </div>
      <div
        className="text-brand-cream text-button-small transition duration-long flex gap-1 items-center"
        style={{
          opacity: isPlaying ? 1 : 0,
        }}
      >
        {item.exploreCta}
        <ArrowIcon size="small" />
      </div>
    </div>
  );
};

const MobileContent = ({ item }: { item: TPartnershipListItem }) => {
  return (
    <div>
      <span className="text-brand-cream text-body-regular">
        {item?.eyebrowText}
      </span>
      <h3 className="text-heading-3 mb-8">{item.title}</h3>
    </div>
  );
};

const PosterWithLogo = ({
  item,
  isPlaying,
}: {
  item: TPartnershipListItem;
  isPlaying: boolean;
}) => {
  return (
    <div
      className="h-full transition duration-long"
      style={{ opacity: isPlaying ? 0 : 1 }}
    >
      <div
        className="absolute w-full h-full top-0 left-0 bg-brand-black rounded-lg z-20"
        style={{ opacity: 0.4 }}
      />
      <div className="flex justify-center items-center h-full">
        {!!item?.logo?.url && <img src={item.logo.url} className="z-20 w-36" />}
      </div>
      {!!item?.videoPosterImage?.url && (
        <img
          className="w-full h-full rounded-lg absolute top-0 left-0 z-10 object-cover"
          src={item?.videoPosterImage?.url}
        />
      )}
    </div>
  );
};

export default PartnershipListItem;
